/**
 * Phase 4.11 — exact-match search support for the Feed.
 *
 * The Mini App's job list is server-filtered by the API's
 * ``/api/v1/jobs?search=`` substring match. That's fine for «find
 * anything containing X», but operators with a long tag like
 * ``smoke_4.7+4.8_first_real`` also need to ask for an *exact*
 * tag, because the substring search matches both that tag AND a
 * derived series ``V2.smoke_4.7+4.8_first_real``.
 *
 * Standard search-bar UX (Google, GitHub, Bing): a query wrapped in
 * double quotes means «exact match». We adopt the same convention.
 *
 * Two-layer filter:
 *   1. ``parseSearchQuery(raw)`` — strips the wrapping quotes and
 *      returns the un-quoted form for the server-side substring
 *      filter (``apiSearch``) plus a boolean flag (``exact``) and the
 *      lower-cased exact term.
 *   2. ``matchesQuery(item, raw)`` — client-side post-filter applied
 *      to the rows the server returned. When ``raw`` had quotes,
 *      only rows whose tag / station_code / id ID equals the term
 *      survive. Without quotes the post-filter is a no-op (the
 *      server already did its substring filtering).
 *
 * The two functions are kept tiny + pure so the unit tests don't
 * need a DOM or any React state.
 */

export interface SearchableItem {
  id: number;
  tag?: string | null;
  station_code?: string | null;
}

export interface ParsedQuery {
  /** What to send to the server's ``/api/v1/jobs?search=`` filter.
   *  Stripped of wrapping quotes when the user asked for exact
   *  match — keeps the server-side substring narrowing useful even
   *  in exact-match mode (cheaper than fetching everything). */
  apiSearch: string;
  /** True when the raw query had wrapping ``"..."``. */
  exact: boolean;
  /** Lower-cased term to compare against in exact mode. Empty
   *  string when ``exact === false`` so callers can avoid an extra
   *  branch when they don't care. */
  exactTerm: string;
}

/** Parse a raw search query, recognising the wrapping-quotes
 *  exact-match convention.
 *
 *  Whitespace around the quotes is tolerated. An empty body
 *  (``""``) is treated the same as no query — there's no useful
 *  semantic for «match the empty string exactly».
 */
export function parseSearchQuery(raw: string): ParsedQuery {
  const trimmed = raw.trim();
  // ``^"(.+)"$`` — at least one character inside the quotes; the
  // empty-quotes case ``""`` falls through to the substring branch
  // where ``apiSearch`` is the literal ``""`` (which the API
  // treats as substring-of-everything, same as a missing param).
  const exactMatch = trimmed.match(/^"(.+)"$/);
  // ``.match`` types capture groups as ``string | undefined`` even
  // when the regex guarantees one. The ``(.+)`` group always matches
  // when the outer match succeeded, so we coerce via the nullish
  // fallback to keep TypeScript's strict flag happy without a runtime
  // branch.
  if (exactMatch) {
    const inner = exactMatch[1] ?? '';
    return {
      apiSearch: inner,
      exact: true,
      exactTerm: inner.toLowerCase(),
    };
  }
  return {
    apiSearch: trimmed,
    exact: false,
    exactTerm: '',
  };
}

/** Client-side post-filter for a row returned from the server.
 *
 *  In *exact* mode (raw had ``"..."``) — return true only when one of
 *  ``item.tag`` / ``item.station_code`` / ``String(item.id)`` is
 *  case-insensitively equal to the exact term.
 *
 *  In *substring* mode — return true when any of the same three
 *  fields contains the term. (The server already filtered to this
 *  set, so the substring branch is mostly a no-op; we keep it for
 *  symmetry, so callers that need a uniform predicate can use the
 *  function on cached / pre-fetched data too.)
 *
 *  An empty / whitespace-only raw query matches every item.
 */
export function matchesQuery(item: SearchableItem, raw: string): boolean {
  if (!raw.trim()) return true;
  const parsed = parseSearchQuery(raw);
  const tag = (item.tag ?? '').toLowerCase();
  const station = (item.station_code ?? '').toLowerCase();
  const idStr = String(item.id);

  if (parsed.exact) {
    return (
      tag === parsed.exactTerm ||
      station === parsed.exactTerm ||
      idStr === parsed.exactTerm
    );
  }
  const needle = parsed.apiSearch.toLowerCase();
  return tag.includes(needle) || station.includes(needle) || idStr.includes(needle);
}
